import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/layouts/_list_page.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { DivAttributes } from '../utils/types'

const cx = bind(styles)

export namespace ControlVariantNS {
  export const alignRight = 'align-right'
  export const block = 'block'
  export const search = 'search'
}

type ControlVariant =
  | typeof ControlVariantNS.alignRight
  | typeof ControlVariantNS.block
  | typeof ControlVariantNS.search

interface Props {
  children: React.ReactNode
  className?: string
  style?: object
}

interface ControlProps extends Props {
  alignRight?: boolean
  block?: boolean
  search?: boolean
  variant: ControlVariant
}

export const ListPage = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-l-list-page', className)} {...props}>
      {children}
    </div>
  )
)

export const Main = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-list-page__main', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Banner = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-list-page__banner', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const ToolHeader = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-l-list-page__tool-header', className)}
    {...props}
  >
    {children}
  </div>
))

export const ContentControls = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-l-list-page__content-controls', className)}
    {...props}
  >
    {children}
  </div>
))

export const Body = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-list-page__body', className)}
      {...props}
    >
      <div className={cx('core-l-list-page__body__content')}>{children}</div>
    </div>
  )
)

export const Control = React.forwardRef<
  HTMLDivElement,
  DivAttributes & ControlProps
>(
  (
    {
      alignRight = false,
      block = false,
      children,
      className,
      search = false,
      variant,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cx(
        'core-l-list-page__content-controls__control',
        {
          [`core-l-list-page__content-controls__control--${variant}`]: variant,
          [`core-l-list-page__content-controls__control--align-right`]: alignRight,
          [`core-l-list-page__content-controls__control--block`]: block,
          [`core-l-list-page__content-controls__control--search`]: search,
        },
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)

Banner.displayName = 'ListPage.Banner'

Body.displayName = 'ListPage.Body'

ContentControls.displayName = 'ListPage.ContentControls'

Control.displayName = 'ListPage.Control'

Main.displayName = 'ListPage.Main'

ToolHeader.displayName = 'ListPage.ToolHeader'

export default addSubcomponents(
  {
    Banner,
    Body,
    ContentControls,
    Control,
    Main,
    ToolHeader,
  },
  ListPage
)
